
// ==========================================
// NEW USER FRIENDLY UI IMPLEMENTATION
// ==========================================

import React, { useState, useEffect } from 'react';
import { Calendar, Modal, Button as AntButton, Row, Col, Select, message, Card, Badge, Tooltip, Input, Empty, Tag, Statistic } from 'antd';
import moment from 'moment';
import { Users, Save, Calendar as CalendarIcon, Search, UserCheck, UserX, Coffee, Sun, CheckCircle, AlertCircle } from "lucide-react";
import Axios from '../../../services/AxiosInterceptor';


const Attendance = () => {
  // State Management
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState('Present'); // Default to Present
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [staff, setStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch Data
  const fetchStaffData = async () => {
    try {
      const response = await Axios.get('api/staff');
      setStaff(response.data);
    } catch (error) {
      console.error("Error fetching staff data:", error);
      message.error("Failed to load staff list");
    }
  };

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const response = await Axios.get("get_api/staff-attendance");
      setAttendanceData(response.data);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      message.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffData();
    fetchAttendanceData();
  }, []);

  // Handlers
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setIsModalVisible(true);
  };

  const saveToBackend = async (data) => {
    try {
      const hide = message.loading('Saving...', 0);
      const response = await Axios.post("api/staff-attendance", data, {
        headers: { "Content-Type": "application/json" }
      });
      hide();

      if (response && response.status === 201) {
        message.success("Saved successfully!");
      } else {
        message.error("Could not save changes.");
      }
    } catch (error) {
      console.error("Error sending attendance data:", error);
      message.error("Failed to connect to server.");
    }
  };

  const handleSaveAttendance = () => {
    if (!attendanceStatus || !selectedStaffId || !selectedDate) {
      message.warning('Please select a status');
      return;
    }

    const formattedDate = selectedDate.format('YYYY-MM-DD');
    let newData = [];

    setAttendanceData((prevData) => {
      const staffIndex = prevData.findIndex((entry) => entry.staffId === selectedStaffId);
      let updatedData = [...prevData];

      if (staffIndex === -1) {
        updatedData.push({
          staffId: selectedStaffId,
          dates: [{ date: formattedDate, status: attendanceStatus }],
        });
      } else {
        const existingRecord = updatedData[staffIndex];
        const dateIndex = existingRecord.dates.findIndex((entry) => entry.date === formattedDate);

        if (dateIndex === -1) {
          existingRecord.dates.push({ date: formattedDate, status: attendanceStatus });
        } else {
          existingRecord.dates[dateIndex].status = attendanceStatus;
        }
      }
      newData = updatedData; // Capture the new data
      return updatedData;
    });

    setIsModalVisible(false);
    // Auto-save the new data immediately
    saveToBackend(newData);
  };

  // Filtered Staff List
  const filteredStaff = staff.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Custom Calendar Cell
  const dateCellRender = (value) => {
    const dateKey = value.format('YYYY-MM-DD');
    const staffRecord = attendanceData.find((entry) => entry.staffId === selectedStaffId);
    const dateEntry = staffRecord?.dates?.find((d) => moment(d.date).format('YYYY-MM-DD') === dateKey);
    const status = dateEntry?.status;

    if (!status) return null;

    let badgeStatus = 'default';
    let icon = null;

    switch(status) {
      case 'Present': badgeStatus = 'success'; icon = <CheckCircle size={12} />; break;
      case 'Absent': badgeStatus = 'error'; icon = <UserX size={12} />; break;
      case 'Leave': badgeStatus = 'warning'; icon = <Coffee size={12} />; break;
      case 'Holiday': badgeStatus = 'processing'; icon = <Sun size={12} />; break;
      default: break;
    }

    return (
      <div className="flex justify-center mt-1">
        <Tooltip title={status}>
          <Badge status={badgeStatus} text={
            <span className={`text-xs font-medium ${
              status === 'Present' ? 'text-green-600' : 
              status === 'Absent' ? 'text-red-500' : 
              status === 'Leave' ? 'text-orange-500' : 'text-blue-500'
            }`}>
              {status}
            </span>
          } />
        </Tooltip>
      </div>
    );
  };

  // Status Options for Modal
  const statusOptions = [
    { value: 'Present', label: 'Present', icon: <UserCheck className="w-4 h-4 text-green-500" />, color: 'bg-green-50 border-green-200' },
    { value: 'Absent', label: 'Absent', icon: <UserX className="w-4 h-4 text-red-500" />, color: 'bg-red-50 border-red-200' },
    { value: 'Leave', label: 'On Leave', icon: <Coffee className="w-4 h-4 text-orange-500" />, color: 'bg-orange-50 border-orange-200' },
    { value: 'Holiday', label: 'Holiday', icon: <Sun className="w-4 h-4 text-blue-500" />, color: 'bg-blue-50 border-blue-200' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Attendance Manager</h1>
          </div>
          <div className="flex items-center gap-4">
             {/* Auto-save enabled */}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Sidebar: Staff List */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[calc(100vh-8rem)] sticky top-24">
              <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Staff Members</h2>
                <Input 
                  prefix={<Search size={16} className="text-slate-400" />} 
                  placeholder="Search staff..." 
                  className="rounded-lg border-slate-200 hover:border-indigo-300 focus:border-indigo-500"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                {filteredStaff.length > 0 ? (
                  filteredStaff.map((item) => (
                    <button
                      key={item._id}
                      onClick={() => setSelectedStaffId(item._id)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all duration-200 group ${
                        selectedStaffId === item._id
                          ? 'bg-indigo-50 border-indigo-200 shadow-sm ring-1 ring-indigo-200'
                          : 'hover:bg-slate-50 border border-transparent hover:border-slate-200'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                        selectedStaffId === item._id 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                      }`}>
                        {item.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-left">
                        <p className={`font-medium text-sm ${selectedStaffId === item._id ? 'text-indigo-900' : 'text-slate-700'}`}>
                          {item.name}
                        </p>
                        <p className="text-xs text-slate-400 truncate max-w-[140px]">{item.position || 'Staff Member'}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                    <UserX size={32} className="mb-2 opacity-50" />
                    <p className="text-sm">No staff found</p>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-slate-100 bg-slate-50 text-xs text-center text-slate-400">
                {filteredStaff.length} members found
              </div>
            </div>
          </div>

          {/* Main Content: Calendar */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-[600px]">
              {!selectedStaffId ? (
                <div className="flex flex-col items-center justify-center h-[600px] bg-slate-50/50">
                  <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mb-6 animate-pulse">
                    <Users className="w-10 h-10 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Select a Staff Member</h3>
                  <p className="text-slate-500 max-w-sm text-center">
                    Choose a staff member from the list on the left to view and manage their attendance records.
                  </p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">
                        {staff.find(s => s._id === selectedStaffId)?.name}'s Attendance
                      </h2>
                      <p className="text-sm text-slate-500">Click on a date to update status</p>
                    </div>
                    <div className="flex gap-2">
                      {['Present', 'Absent', 'Leave', 'Holiday'].map(status => (
                        <div key={status} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200">
                          <div className={`w-2 h-2 rounded-full ${
                            status === 'Present' ? 'bg-green-500' :
                            status === 'Absent' ? 'bg-red-500' :
                            status === 'Leave' ? 'bg-orange-500' : 'bg-blue-500'
                          }`} />
                          <span className="text-xs font-medium text-slate-600">{status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Calendar 
                    dateCellRender={dateCellRender} 
                    onSelect={handleDateSelect}
                    className="custom-calendar"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modern Modal */}
      <Modal
        title={null}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
        width={400}
        className="rounded-2xl overflow-hidden"
      >
        <div className="text-center mb-6">
          <h3 className="text-lg font-bold text-slate-800">
            {selectedDate?.format('MMMM Do, YYYY')}
          </h3>
          <p className="text-slate-500 text-sm">Select attendance status</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setAttendanceStatus(option.value)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                attendanceStatus === option.value
                  ? `border-indigo-600 bg-indigo-50 text-indigo-900`
                  : `border-slate-100 hover:border-indigo-200 hover:bg-slate-50 text-slate-600`
              }`}
            >
              <div className={`p-2 rounded-full ${attendanceStatus === option.value ? 'bg-white' : 'bg-slate-100'}`}>
                {option.icon}
              </div>
              <span className="font-medium text-sm">{option.label}</span>
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <AntButton 
            block 
            size="large" 
            onClick={() => setIsModalVisible(false)}
            className="rounded-xl h-12"
          >
            Cancel
          </AntButton>
          <AntButton 
            block 
            type="primary" 
            size="large" 
            onClick={handleSaveAttendance}
            className="bg-indigo-600 hover:bg-indigo-700 rounded-xl h-12 font-medium"
          >
            Confirm Update
          </AntButton>
        </div>
      </Modal>

      {/* Global Styles for Calendar Override */}
      <style jsx global>{`
        .ant-picker-calendar-full .ant-picker-panel .ant-picker-calendar-date-content {
          height: 60px;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};


export default Attendance;
